import React, { useEffect, useState } from 'react';
import api from '../lib/api';
import { Card, CardContent } from './ui/card';
import { CalendarCheck, CalendarX, CalendarClock, AlertTriangle } from 'lucide-react';

const timeAgo = (iso) => {
  if (!iso) return 'never';
  const then = new Date(iso).getTime();
  const mins = Math.max(0, Math.round((Date.now() - then) / 60000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs === 1 ? '' : 's'} ago`;
  const days = Math.round(hrs / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
};

const STATE = {
  healthy: {
    icon: CalendarCheck,
    ring: 'border-emerald-300 bg-emerald-50/60',
    chip: 'bg-emerald-100 text-emerald-700',
    iconWrap: 'bg-emerald-100 text-emerald-600',
    label: 'Landing in Outlook',
  },
  not_connected: {
    icon: AlertTriangle,
    ring: 'border-amber-300 bg-amber-50/60',
    chip: 'bg-amber-100 text-amber-700',
    iconWrap: 'bg-amber-100 text-amber-600',
    label: 'Microsoft 365 not connected',
  },
  error: {
    icon: CalendarX,
    ring: 'border-red-300 bg-red-50/60',
    chip: 'bg-red-100 text-red-700',
    iconWrap: 'bg-red-100 text-red-600',
    label: "Last booking didn't reach Outlook",
  },
  unknown: {
    icon: CalendarClock,
    ring: 'border-slate-200 bg-slate-50/60',
    chip: 'bg-slate-100 text-slate-600',
    iconWrap: 'bg-slate-100 text-slate-500',
    label: 'No bookings yet',
  },
};

/**
 * Dashboard tile: confirms website call bookings are reaching the Outlook
 * calendar, showing when the last booking was received.
 */
export const BookingHealthTile = () => {
  const [data, setData] = useState(null);

  const load = () => {
    api.get('/website-form/booking-health')
      .then((r) => setData(r.data))
      .catch(() => {});
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);

  if (!data) return null;

  const s = STATE[data.health] || STATE.unknown;
  const Icon = s.icon;

  return (
    <Card className={`${s.ring} border`} data-testid="booking-health-tile">
      <CardContent className="p-5">
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${s.iconWrap}`}>
            <Icon className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="font-semibold text-slate-900">Website bookings → Outlook</p>
              <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${s.chip}`} data-testid="booking-health-status">
                {s.label}
              </span>
            </div>
            <p className="text-sm text-slate-600 mt-1" data-testid="booking-health-last-received">
              {data.total_bookings > 0
                ? <>Last booking received <strong>{timeAgo(data.last_received_at)}</strong>{data.last_customer ? ` — ${data.last_customer}` : ''}.</>
                : 'No website call bookings received yet.'}
            </p>
            {data.total_bookings > 0 && (
              <p className="text-xs text-slate-400 mt-0.5">
                {data.last_7_days} in the last 7 days · {data.total_bookings} all time
              </p>
            )}
            {data.health === 'not_connected' && (
              <p className="text-xs text-amber-600 mt-1">
                Bookings are being logged, but calendar invites won't send until Microsoft 365 is connected.
              </p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default BookingHealthTile;
